import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { TablePaginationProps } from './Pagination.v4.props';

const MAX_VISIBLE_PAGES = 7;

const buildPageNumbers = (current: number, total: number): (number | 'ellipsis')[] => {
  if (total <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (current <= 4) {
    pages.push(2, 3, 4, 5, 'ellipsis', total);
  } else if (current >= total - 3) {
    pages.push('ellipsis', total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push('ellipsis', current - 1, current, current + 1, 'ellipsis', total);
  }

  return pages;
};

const PAGE_BTN_BASE = {
  minWidth: 32,
  height: 32,
  fontSize: '13px',
  fontWeight: 500,
  border: '1px solid',
  borderColor: 'v4.border',
  borderRadius: 1,
  px: 1,
} as const;

export const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pages = useMemo(() => buildPageNumbers(page, totalPages), [page, totalPages]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 1.5,
        px: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button
          size="small"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
          sx={{ ...PAGE_BTN_BASE, bgcolor: 'background.paper', color: 'text.primary', '&:hover': { bgcolor: 'v4.tableEven' } }}
        >
          &#8249;
        </Button>
        {pages.map((p, idx) =>
          p === 'ellipsis' ? (
            <Typography
              key={`ellipsis-${idx}`}
              sx={{ px: 0.5, fontSize: '13px', color: 'text.secondary' }}
            >
              ...
            </Typography>
          ) : (
            <Button
              key={p}
              size="small"
              variant={p === page ? 'contained' : 'text'}
              onClick={() => onPageChange(p)}
              aria-label={`Page ${p}`}
              aria-current={p === page ? 'page' : undefined}
              sx={{
                ...PAGE_BTN_BASE,
                ...(p === page
                  ? {
                      bgcolor: theme.palette.v4.accent,
                      color: 'common.white',
                      borderColor: theme.palette.v4.accent,
                      '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
                    }
                  : {
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'v4.tableEven' },
                    }),
              }}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          size="small"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
          sx={{ ...PAGE_BTN_BASE, bgcolor: 'background.paper', color: 'text.primary', '&:hover': { bgcolor: 'v4.tableEven' } }}
        >
          &#8250;
        </Button>
      </Box>
    </Box>
  );
};
